import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { api } from "./lib/api";
import { Toaster } from "sonner";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { SettingsProvider } from "./context/SettingsContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import Bundles from "./pages/Bundles";
import AdminReset from "./pages/AdminReset";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderSuccess from "./pages/OrderSuccess";
import OrderPending from "./pages/OrderPending";
import Shipping from "./pages/Shipping";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Admin from "./pages/Admin";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import Account from "./pages/Account";
import DigitalShop from "./pages/DigitalShop";
import DigitalOrderPending from "./pages/DigitalOrderPending";
import PrivacyPolicy from "./pages/PrivacyPolicy";

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);
  return null;
}

function ConsentPopup() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem("c4t_privacy_ok")) setVisible(true);
  }, []);
  if (!visible) return null;
  const accept = () => { localStorage.setItem("c4t_privacy_ok", "1"); setVisible(false); };
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-2rem)] max-w-lg">
      <div className="bg-white border-[3px] border-black rounded-2xl shadow-[4px_4px_0_#000] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <p className="text-sm font-semibold flex-1 leading-relaxed">
          We collect your name, email and order details to process purchases, and use analytics
          to improve the site.{" "}
          <Link to="/privacy" onClick={accept} className="text-[#FF1F87] underline font-bold">
            Privacy Policy
          </Link>
        </p>
        <button
          onClick={accept}
          className="shrink-0 bg-[#FF1F87] text-white font-bold border-[2px] border-black rounded-xl px-5 py-2 text-sm shadow-[2px_2px_0_#000] hover:translate-y-[1px] hover:shadow-[1px_1px_0_#000] transition-all">
          That's fine
        </button>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=") || location.hash?.includes("id_token=")) return <AuthCallback />;
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/shop" element={<Layout><Shop /></Layout>} />
      <Route path="/bundles" element={<Layout><Bundles /></Layout>} />
      <Route path="/product/:id" element={<Layout><ProductDetail /></Layout>} />
      <Route path="/cart" element={<Layout><Cart /></Layout>} />
      <Route path="/checkout" element={<Layout><Checkout /></Layout>} />
      <Route path="/order-success" element={<Layout><OrderSuccess /></Layout>} />
      <Route path="/order-pending" element={<Layout><OrderPending /></Layout>} />
      <Route path="/shipping" element={<Layout><Shipping /></Layout>} />
      <Route path="/about" element={<Layout><About /></Layout>} />
      <Route path="/contact" element={<Layout><Contact /></Layout>} />
      <Route path="/login" element={<Layout><Login /></Layout>} />
      <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
      <Route path="/reset-password" element={<Layout><ResetPassword /></Layout>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/account" element={<Layout><Account /></Layout>} />
      <Route path="/digital-shop" element={<Layout><DigitalShop /></Layout>} />
      <Route path="/digital-pending" element={<Layout><DigitalOrderPending /></Layout>} />
      <Route path="/privacy" element={<Layout><PrivacyPolicy /></Layout>} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/reset" element={<AdminReset />} />
    </Routes>
    </>
  );
}

function App() {
  // Keepalive: ping backend on load to pre-warm HuggingFace Spaces cold start
  useEffect(() => {
    api.get("/").catch(() => {});
  }, []);

  return (
    <div className="App">
      <SettingsProvider>
        <CurrencyProvider>
          <AuthProvider>
            <CartProvider>
              <BrowserRouter>
                <Toaster
                  position="top-center"
                  toastOptions={{
                    style: {
                      background: "#fff",
                      border: "3px solid #000",
                      boxShadow: "4px 4px 0 #000",
                      borderRadius: "16px",
                      fontFamily: "Nunito, sans-serif",
                      color: "#000",
                    },
                  }}
                />
                <AppRouter />
                <ConsentPopup />
              </BrowserRouter>
            </CartProvider>
          </AuthProvider>
        </CurrencyProvider>
      </SettingsProvider>
    </div>
  );
}

export default App;
